import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuItem,
  Box,
  Text,
  Icon,
  Input,
  Button,
  ScrollArea,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  measureTextWidth,
} from "@salestrak/ui";
import {
  customFieldValuesEqual,
  dataToField,
  fieldDto,
  getDefaultValueByType,
} from "@salestrak/custom-fields";
import {
  AddCustomFieldsDialog,
} from "#app/components/custom-fields/add-custom-fields-dialog";
import {
  useAvailableCustomFields,
} from "#app/hooks/issue-view/use-available-custom-fields";
import { useIssueViewCapabilities } from "./issue-view-capabilities";
import { SummaryFoldRow, useSummaryEmptyFold } from "./summary-fold-row";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { useScrollToElement } from "#app/lib/hooks/use-scroll-to-element";
import type { CustomFieldDef } from "./issue-view-types";
import type { CustomFieldRow as CustomFieldListRow, IssueRow } from "#app/lib/data";
import {
  getCustomFieldDisplayValue,
  isCustomFieldValueEmpty,
} from "../custom-fields/custom-field-renderers";
import { useOptionPickerOps } from "#app/lib/hooks/use-custom-field-mutations";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import { useCreateIssueMode } from "#app/lib/create-issue-context";
import {
  useCustomFieldsDialogActions,
} from "#app/lib/custom-fields-dialog-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import type { Omittable } from "@salestrak/ui";
import { useTransientValue } from "#app/lib/hooks/use-transient-value";
import {
  CustomFieldRow,
  CUSTOM_FIELD_ROW_HEIGHT,
  DESKTOP_FIELD_COLUMN_WIDTH,
} from "./custom-field-row";
import { AddFieldsDrawer } from "./add-fields-drawer";
import { AnalyticsContext, IssueEvent, trackEvent } from "#app/lib/analytics";

const trackAddFields = (count: number, via: "drawer" | "window") => {
  if (count === 0) return;
  trackEvent({ context: AnalyticsContext.Issue, event: IssueEvent.AddFields, data: { count, via } });
};

const FOCUS_HIGHLIGHT_MS = 3000;
/** The mobile drawer's field column: one width, whatever the labels measure. */
const MOBILE_FIELD_COLUMN_WIDTH = 180;
const MOBILE_VALUE_COLUMN_MIN_WIDTH = 160;
const MOBILE_VALUE_COLUMN_MAX_WIDTH = 260;
/** The mobile drawer's column type: the `sm` rung. */
const MOBILE_COLUMN_TEXT_PX = 14;

type IssueViewCustomFieldsProps = Omittable<{
  issue?: IssueRow;
  values?: Record<string, unknown>;
  customFields?: CustomFieldDef[];
  // Custom field ids attached to the issue type, in server sortOrder. Drives
  // row order in both create and view modes. Fields with values on the issue
  // that aren't in this list (legacy values from fields detached from the
  // type) are appended after the ordered list in view mode.
  orderedTypeFieldIds?: string[];
  onUpdateValue?: (data: { customFieldId: string; value: unknown }) => void;
  // Photos-field delta writes (view mode only — absent in create mode). Routes
  // each landed upload / removed tile to an atomic single-photo add/remove.
  onAddPhoto?: (customFieldId: string, photoId: string) => void | Promise<void>;
  onRemovePhoto?: (customFieldId: string, photoId: string) => void | Promise<void>;
  requiredFieldIds?: string[];
  /** Fields the type keeps on the summary while empty: listed even without a
   *  value record, and never behind the fold. */
  alwaysShownFieldIds?: string[];
  onCloseIssue?: () => void;
  /** Scroll to + briefly highlight this field row on mount (deep-link). */
  focusCustomFieldId?: string | null;
  /** Embedded (widget) mode: the widget card owns the title header, so the
   *  internal header row is not rendered… */
  hideHeader?: boolean;
  /** …and the search value is controlled by the card's header actions. When
   *  set (even ""), it replaces the internal search state. */
  search?: string;
}>;

export function IssueViewCustomFields({
  issue,
  values = {},
  customFields = [],
  orderedTypeFieldIds = [],
  onUpdateValue,
  onAddPhoto,
  onRemovePhoto,
  requiredFieldIds = [],
  alwaysShownFieldIds = [],
  focusCustomFieldId,
  hideHeader,
  search,
}: IssueViewCustomFieldsProps) {
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  // Field management (create/manage dialogs) is gated; adding existing
  // workspace fields to an issue is plain issue editing and stays open.
  const canManageCustomFields = usePermission("custom_fields.manage");
  // One instance for every row's option picker.
  const { onCreateOption: createCustomFieldOption, optionActions } =
    useOptionPickerOps();
  const createCtx = useCreateIssueMode();
  const isCreateMode = !!createCtx;
  const { canEdit } = useIssueViewCapabilities();
  const readOnly = !isCreateMode && !canEdit;
  const submitted = createCtx?.submitted ?? false;
  const requiredSet = useMemo(
    () => new Set(requiredFieldIds),
    [requiredFieldIds],
  );
  // Rows the fold never hides: what the type requires or keeps shown. A
  // reading rule only — `requiredSet` alone drives the create-mode errors.
  const pinnedSet = useMemo(
    () => new Set([...requiredFieldIds, ...alwaysShownFieldIds]),
    [requiredFieldIds, alwaysShownFieldIds],
  );
  const fieldErrors = useMemo(() => {
    if (!submitted) return {};
    const errors: Record<string, string> = {};
    for (const cfId of requiredFieldIds) {
      const val = values[cfId];
      if (val === undefined || val === null || val === "") {
        errors[cfId] = "Required";
      } else if (Array.isArray(val) && val.length === 0) {
        errors[cfId] = "Required";
      }
    }
    return errors;
  }, [submitted, requiredFieldIds, values]);

  const [searchVisible, setSearchVisible] = useState(false);
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const searchValue = search ?? internalSearchValue;
  // The summary-wide empty fold (see `useSummaryEmptyFold`). Search suspends
  // it, and it never applies while no field holds a value — a fold that left
  // the section as a header over a "More" row would hide the whole list.
  const emptyFold = useSummaryEmptyFold();
  const [showOnlyRequired, setShowOnlyRequired] = useState(isCreateMode);
  const isMobile = useIsMobile();
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [openFieldOverlayId, setOpenFieldOverlayId] = useState<string | null>(
    null,
  );
  // Transient highlight for a deep-linked field (inbox mention notification).
  // Purely visual — unlike activeFieldId it does NOT put the row into edit mode.
  const [focusHighlightId, showFocusHighlight] = useTransientValue<string>(FOCUS_HIGHLIGHT_MS);
  // Base UI's autocomplete has no `shouldFilter`/`keywords` pair, so the
  // add-field search is an explicit local query + substring match.
  const [addFieldQuery, setAddFieldQuery] = useState("");

  const customFieldsById = useMemo(() => {
    const map: Record<string, CustomFieldDef> = {};
    for (const f of customFields) {
      map[f.customFieldId] = f;
    }
    return map;
  }, [customFields]);

  // Create mode: render every field attached to the type (in sortOrder),
  // value-or-not, then the fields the creator added by hand — draft values
  // whose key the type does not carry — in the order they were added. Falls
  // back to the legacy createCtx-derived list if the caller didn't pass
  // orderedTypeFieldIds (defensive — IssueViewSummary always passes it).
  const { createModeFieldIds, addedFieldIds } = useMemo(() => {
    if (!isCreateMode) {
      return { createModeFieldIds: [] as string[], addedFieldIds: new Set<string>() };
    }
    const typeIds =
      orderedTypeFieldIds.length > 0
        ? orderedTypeFieldIds
        : createCtx.issueTypeFields
            .filter((f) => f.field !== null)
            .map((f) => f.customFieldId);
    const typeIdSet = new Set(typeIds);
    const added = Object.keys(createCtx.draft.values).filter(
      (cfId) => cfId !== "." && !typeIdSet.has(cfId) && customFieldsById[cfId],
    );
    return {
      createModeFieldIds: [...typeIds, ...added],
      // Hand-added fields are never folded behind "required only".
      addedFieldIds: new Set(added),
    };
  }, [isCreateMode, createCtx, orderedTypeFieldIds, customFieldsById]);

  // Transform raw values through dataToField
  const formValues = useMemo(() => {
    if (!values) return {} as Record<string, unknown>;
    return Object.keys(values).reduce<Record<string, unknown>>((acc, cfId) => {
      if (cfId === ".") return acc;
      const field = customFieldsById[cfId];
      if (!field) return acc;
      acc[cfId] = dataToField(field.type, values[cfId]).fieldValue;
      return acc;
    }, {});
  }, [values, customFieldsById]);

  // View mode: keep the same SET of rendered fields (those with a value
  // record on the issue) but order them by the issue type's sortOrder.
  // Fields whose customFieldId isn't in the type's ordered list (e.g. the
  // field was detached from the type after a value was stored) trail at
  // the bottom in their existing arbitrary order.
  const fieldIds = useMemo(() => {
    if (isCreateMode) return createModeFieldIds;
    const valueKeys = Object.keys(formValues);
    if (orderedTypeFieldIds.length === 0) return valueKeys;
    // A type field the type keeps shown is listed even before it holds a
    // value record — the row is the invitation to fill it.
    const valueKeySet = new Set([
      ...valueKeys,
      ...alwaysShownFieldIds.filter((id) => customFieldsById[id]),
    ]);
    const ordered = orderedTypeFieldIds.filter((id) => valueKeySet.has(id));
    const orderedSet = new Set(ordered);
    const legacy = valueKeys.filter((id) => !orderedSet.has(id));
    return [...ordered, ...legacy];
  }, [
    isCreateMode,
    createModeFieldIds,
    formValues,
    orderedTypeFieldIds,
    alwaysShownFieldIds,
    customFieldsById,
  ]);

  // Phone/address can carry metadata with no user-entered value — isCustomFieldValueEmpty treats those as empty.
  const fieldsWithValues = useMemo(
    () =>
      fieldIds.filter((id) => {
        const field = customFieldsById[id];
        return field ? !isCustomFieldValueEmpty(field, formValues[id]) : false;
      }),
    [customFieldsById, fieldIds, formValues],
  );

  // A row on screen stays on screen while the fold holds still: filling one
  // field (which makes the section foldable) or clearing one must not fold
  // the empties away under the cursor. The set is this open's — the section
  // remounts per project — and a fold press starts it over, so "Less" still
  // tucks the empties away.
  const keptRowsRef = useRef({ collapsed: emptyFold.collapsed, ids: new Set<string>() });
  if (keptRowsRef.current.collapsed !== emptyFold.collapsed) {
    keptRowsRef.current = { collapsed: emptyFold.collapsed, ids: new Set<string>() };
  }
  const keptRowIds = keptRowsRef.current.ids;

  // Only the empties the fold can actually hide count toward it — a pinned
  // or kept empty row stays up, so it is not "more".
  const fieldsWithValuesSet = useMemo(() => new Set(fieldsWithValues), [fieldsWithValues]);
  const numberOfEmptyFields = fieldIds.filter(
    (id) => !fieldsWithValuesSet.has(id) && !pinnedSet.has(id) && !keptRowIds.has(id),
  ).length;
  const foldable =
    !isCreateMode && !searchValue && numberOfEmptyFields > 0 && fieldsWithValues.length > 0;
  const hideEmpty = foldable && emptyFold.collapsed;

  // Fields to display based on show/hide empty toggle (view) or required filter (create)
  const displayedFieldIds = useMemo(() => {
    let base: string[];
    if (searchValue) {
      base = fieldIds;
    } else if (isCreateMode) {
      // Only collapse to required-only when there actually are required
      // fields — otherwise the filter hides every row (and the "Show all
      // fields" toggle doesn't render), leaving an empty header table.
      base = showOnlyRequired && requiredSet.size > 0
        ? fieldIds.filter((id) => requiredSet.has(id) || addedFieldIds.has(id))
        : fieldIds;
    } else {
      base = hideEmpty
        ? fieldIds.filter(
            (id) => fieldsWithValuesSet.has(id) || pinnedSet.has(id) || keptRowIds.has(id),
          )
        : fieldIds;
    }
    if (searchValue) {
      const needle = searchValue.toLowerCase();
      base = base.filter((id) => {
        const field = customFieldsById[id];
        return (
          field &&
          (field.name.toLowerCase().includes(needle) ||
            field.type.toLowerCase().includes(needle))
        );
      });
    }
    return base;
  }, [
    fieldIds,
    fieldsWithValuesSet,
    pinnedSet,
    keptRowIds,
    hideEmpty,
    showOnlyRequired,
    isCreateMode,
    requiredSet,
    addedFieldIds,
    searchValue,
    customFieldsById,
  ]);

  const hasFields = fieldIds.length > 0;

  // Search narrows the list for a moment; only the unsearched list is kept.
  // Expanded ("More"), every row shows and none can fold away, so nothing is
  // kept: keeping them all would leave no empties to fold and take "Less"
  // off the screen.
  const foldCollapsed = emptyFold.collapsed;
  useEffect(() => {
    if (isCreateMode || searchValue || !foldCollapsed) return;
    for (const id of displayedFieldIds) keptRowIds.add(id);
  }, [isCreateMode, searchValue, foldCollapsed, displayedFieldIds, keptRowIds]);

  useEffect(() => {
    if (!activeFieldId) return;
    if (!displayedFieldIds.includes(activeFieldId)) {
      setActiveFieldId(null);
    }
  }, [activeFieldId, displayedFieldIds]);

  // Deep-link focus: when opened from a paragraph-mention inbox item, scroll
  // to the field and briefly highlight it. Runs once per focus id, and only
  // once the issue's field set has loaded (so the row exists to scroll to).
  const { scrollTo } = useScrollToElement();
  const focusedFieldRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusCustomFieldId) {
      // A cleared focus re-arms the guard so the SAME field can be focused
      // again later (media-tab quick links clear between jumps).
      focusedFieldRef.current = null;
      return;
    }
    if (focusedFieldRef.current === focusCustomFieldId) return;
    if (!fieldIds.includes(focusCustomFieldId)) return; // wait for values to load
    focusedFieldRef.current = focusCustomFieldId;
    // Unfold so a focused-but-empty target still renders.
    if (!fieldsWithValues.includes(focusCustomFieldId)) emptyFold.setCollapsed(false);
    showFocusHighlight(focusCustomFieldId);
    scrollTo(`cf-${focusCustomFieldId}`);
  }, [focusCustomFieldId, fieldIds, fieldsWithValues, scrollTo, emptyFold, showFocusHighlight]);

  const handleRemoveField = useCallback(
    (customFieldId: string) => {
      if (invalidFieldIdRef.current === customFieldId) {
        invalidFieldIdRef.current = null;
      }
      setActiveFieldId((current) =>
        current === customFieldId ? null : current,
      );
      onUpdateValue?.({ customFieldId, value: undefined });
    },
    [onUpdateValue],
  );

  const handleFieldUpdate = useCallback(
    (customFieldId: string, value: unknown) => {
      const field = customFieldsById[customFieldId];
      if (!field) return;
      try {
        if (value === undefined) {
          onUpdateValue?.({ customFieldId, value: undefined });
          return;
        }
        const next = fieldDto(field.type, value);
        // A renderer commits on every blur, edited or not. The value the
        // issue already holds is not a write — nothing to store, and no
        // activity may say a value changed.
        if (customFieldValuesEqual(values[customFieldId], next)) return;
        onUpdateValue?.({ customFieldId, value: next });
      } catch (error) {
        console.warn("fieldDto error:", error);
      }
    },
    [customFieldsById, onUpdateValue, values],
  );

  const handleCloseSearch = useCallback(() => {
    setInternalSearchValue("");
    setSearchVisible(false);
  }, []);

  // Tracks which row (if any) currently holds an invalid value. A ref because
  // the synchronous flow on a click-to-another-cell is: row A's input blurs →
  // commitFromRenderer marks row A invalid → row B's click handler runs (this
  // tick, no render yet) → handleActivateField fires. State wouldn't be visible
  // here yet; the ref is.
  const invalidFieldIdRef = useRef<string | null>(null);
  const handleInvalidStateChange = useCallback(
    (customFieldId: string, invalid: boolean) => {
      if (invalid) {
        invalidFieldIdRef.current = customFieldId;
      } else if (invalidFieldIdRef.current === customFieldId) {
        invalidFieldIdRef.current = null;
      }
    },
    [],
  );

  const handleActivateField = useCallback(
    (customFieldId: string) => {
      // Refuse to switch active rows while another row is stuck on an invalid
      // value — otherwise that row's edit cell would tear down despite its
      // local invalidRef gate, because activeFieldId is parent-owned.
      if (
        invalidFieldIdRef.current &&
        invalidFieldIdRef.current !== customFieldId
      ) {
        return;
      }
      setActiveFieldId(customFieldId);
      setOpenFieldOverlayId((current) =>
        current && current !== customFieldId ? null : current,
      );
    },
    [],
  );

  const handleDeactivateField = useCallback(() => {
    if (invalidFieldIdRef.current) return;
    setActiveFieldId(null);
  }, []);

  const handleNavigateToCreateField = useCallback(() => {
    setAddFieldDialogOpen(false);
    openCustomFieldsDialog();
  }, [openCustomFieldsDialog]);

  const handleOpenAddFieldDialog = useCallback(() => {
    setSelectedFieldIds([]);
    setAddFieldDialogOpen(true);
  }, []);

  const handleRowHighlightChange = useCallback(
    (customFieldId: string, open: boolean) => {
      setOpenFieldOverlayId((current) => {
        if (open) return customFieldId;
        return current === customFieldId ? null : current;
      });
    },
    [],
  );

  const highlightedFieldId = openFieldOverlayId ?? activeFieldId ?? focusHighlightId;

  const mobileFieldColumnWidth = isMobile ? MOBILE_FIELD_COLUMN_WIDTH : undefined;

  const mobileValueColumnWidth = useMemo(() => {
    if (!isMobile) return undefined;
    return displayedFieldIds.reduce((maxWidth, cfId) => {
      const field = customFieldsById[cfId];
      const sourceValue = isCreateMode ? values[cfId] : formValues[cfId];
      const text = field
        ? getCustomFieldDisplayValue(field, sourceValue)
        : NONE_OPTION_LABEL;
      const estimatedWidth = Math.min(
        measureTextWidth(text, { sizePx: MOBILE_COLUMN_TEXT_PX }) + 96,
        MOBILE_VALUE_COLUMN_MAX_WIDTH,
      );
      return Math.max(maxWidth, estimatedWidth);
    }, MOBILE_VALUE_COLUMN_MIN_WIDTH);
  }, [
    customFieldsById,
    displayedFieldIds,
    formValues,
    isCreateMode,
    isMobile,
    values,
  ]);

  const handleAddFields = useCallback(() => {
    trackAddFields(selectedFieldIds.length, "drawer");
    for (const cfId of selectedFieldIds) {
      const defaultValue = getDefaultValueByType(customFieldsById[cfId]?.type);
      onUpdateValue?.({ customFieldId: cfId, value: defaultValue });
    }
    setAddFieldDialogOpen(false);
    setSelectedFieldIds([]);
  }, [selectedFieldIds, customFieldsById, onUpdateValue]);

  const toggleFieldSelection = useCallback((cfId: string) => {
    setSelectedFieldIds((prev) =>
      prev.includes(cfId) ? prev.filter((id) => id !== cfId) : [...prev, cfId],
    );
  }, []);

  // The window picker hands whole rows back, so the type default needs no
  // field-bag lookup — the picked row carries its own type.
  const handleAddFieldRows = useCallback(
    (rows: CustomFieldListRow[]) => {
      trackAddFields(rows.length, "window");
      for (const row of rows) {
        onUpdateValue?.({
          customFieldId: row.customFieldId,
          value: getDefaultValueByType(row.type),
        });
      }
      setAddFieldDialogOpen(false);
    },
    [onUpdateValue],
  );

  const allAvailableFields = useAvailableCustomFields(fieldIds);

  // The command list does no filtering of its own, so the match set —
  // name, key, type and type label — is computed here.
  const availableFields = useMemo(() => {
    const q = addFieldQuery.trim().toLowerCase();
    if (!q) return allAvailableFields;
    return allAvailableFields.filter((field) =>
      [field.name, field.key ?? "", field.type, getFieldTypeMeta(field.type).label]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [allAvailableFields, addFieldQuery]);

  const headerActions = (
    <Box direction="row" align="center" gap="xs">
      {hasFields && !searchVisible && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Search Custom Fields"
                onClick={() => setSearchVisible(true)}
              />
            }
          >
            <Icon name="search" size="md" />
          </TooltipTrigger>
          <TooltipPopup side="top">Search</TooltipPopup>
        </Tooltip>
      )}
      {searchVisible && (
        <Box direction="row" align="center" gap="xs">
          <Box direction="row" align="center" width={180}>
            <Input
              value={searchValue}
              onChange={(event) => setInternalSearchValue(event.target.value)}
              placeholder="Search..."
              size="sm"
              autoFocus
            />
          </Box>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close search"
                  onClick={handleCloseSearch}
                />
              }
            >
              <Icon name="x" size="md" />
            </TooltipTrigger>
            <TooltipPopup side="top">Close</TooltipPopup>
          </Tooltip>
        </Box>
      )}
      {!readOnly && (
        <Menu>
          {/* MenuTrigger's prop set is closed, so the TooltipTrigger sits
              INSIDE its render chain — the tooltip's hover handlers merge
              down onto the button element itself. */}
          <Tooltip>
            <MenuTrigger
              render={
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Add custom field"
                    />
                  }
                />
              }
            >
              <Icon name="plus" size="md" />
            </MenuTrigger>
            <TooltipPopup side="top">Add field</TooltipPopup>
          </Tooltip>
          <MenuPopup align="end">
            {canManageCustomFields && (
              <MenuItem onClick={handleNavigateToCreateField}>
                <Icon name="plus" size="md" />
                <Text>Create field...</Text>
              </MenuItem>
            )}
            <MenuItem onClick={handleOpenAddFieldDialog}>
              <Icon name="book-open" size="md" />
              <Text>Add field from workspace...</Text>
            </MenuItem>
          </MenuPopup>
        </Menu>
      )}
    </Box>
  );

  // No frame of its own: the rows sit directly on the host surface with their
  // hairline dividers, and each row's hover wash bleeds past the content edge
  // (the InlineRow surface). Inside a widget card (`hideHeader`) the card's
  // header text is the alignment rail, so the list carries no inset; the
  // standalone header path insets header and rows by the same `md`.
  const sectionInset = hideHeader ? undefined : "md";

  return (
    <Box gap="sm" group="box">
      {hideHeader ? null : (
        <Box
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          paddingX="md"
          paddingTop="sm"
        >
          <Text size="lg" weight="semibold">
            Custom fields
          </Text>
          {headerActions}
        </Box>
      )}

      {/* Fields list or empty state */}
      {hasFields ? (
        <Box direction="column" paddingX={sectionInset}>
          {(() => {
            const tableContent = (
              <>
                {displayedFieldIds.map((cfId, index) => {
                  const field = customFieldsById[cfId];
                  const nextFieldId = displayedFieldIds[index + 1] ?? null;
                  return (
                    <CustomFieldRow
                      key={cfId}
                      id={`cf-${cfId}`}
                      cfId={cfId}
                      field={field}
                      value={isCreateMode ? values[cfId] : formValues[cfId]}
                      isActive={activeFieldId === cfId}
                      fieldColumnWidth={mobileFieldColumnWidth}
                      valueColumnWidth={mobileValueColumnWidth}
                      required={requiredSet.has(cfId)}
                      error={fieldErrors[cfId]}
                      // A draft's type fields are the type's to carry; only
                      // the fields the creator added can come back off.
                      showRemove={
                        !readOnly && (!isCreateMode || addedFieldIds.has(cfId))
                      }
                      // The first row closes the list's top edge with the
                      // same hairline the rows draw below themselves.
                      topDivider={index === 0}
                      highlighted={highlightedFieldId === cfId}
                      beforeHighlighted={highlightedFieldId === nextFieldId}
                      onRowHighlightChange={handleRowHighlightChange}
                      onInvalidStateChange={handleInvalidStateChange}
                      onUpdate={handleFieldUpdate}
                      onAddPhoto={onAddPhoto}
                      onRemovePhoto={onRemovePhoto}
                      onRemove={handleRemoveField}
                      onActivate={handleActivateField}
                      onDeactivate={handleDeactivateField}
                      onCreateOption={createCustomFieldOption}
                      optionActions={optionActions}
                      readOnly={readOnly}
                      downloadPrefix={issue?.name}
                    />
                  );
                })}
              </>
            );

            if (!isMobile) return tableContent;

            const minTableWidth =
              (mobileFieldColumnWidth ?? DESKTOP_FIELD_COLUMN_WIDTH) +
              (mobileValueColumnWidth ?? MOBILE_VALUE_COLUMN_MIN_WIDTH);

            return (
              // The scrollbar IS user-visible chrome here (a horizontally
              // scrolled table on mobile), so this is a ScrollArea, not a plain
              // overflow Box. ScrollArea renders both scrollbars and reveals
              // whichever axis overflows.
              // `clampContentMinWidth={false}` is required: the default pins
              // the content to `min-width: 0`, which would cancel the table's
              // intrinsic width and kill the horizontal scroll.
              <ScrollArea className="w-full" clampContentMinWidth={false}>
                <Box minWidth={minTableWidth}>{tableContent}</Box>
              </ScrollArea>
            );
          })()}
        </Box>
      ) : // Empty: nothing. The header's + (the widget card's header actions,
      // or the standalone header's own add menu) is already the add
      // affordance, and a second full-width prompt says it twice. A create
      // draft is no different — its type's fields are what the list shows,
      // and the creator adds more the way a settled Project does.
      null}

      {/* Show/hide fields toggle */}
      {searchValue ? null : isCreateMode &&
        requiredSet.size > 0 &&
        fieldIds.length > requiredSet.size ? (
        <Box direction="row" paddingX="md" paddingBottom="md">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowOnlyRequired((prev) => !prev)}
          >
            <Icon
              name={showOnlyRequired ? "chevron-down" : "chevron-up"}
              size="md"
              color="muted"
            />
            <Text size="sm" variant="muted">
              {showOnlyRequired ? "Show all fields" : "Show required fields"}
            </Text>
          </Button>
        </Box>
      ) : foldable ? (
        // Keep the fold control on the field label axis in both states.
        <Box paddingX={sectionInset}>
          <Box paddingLeft="lg" height={CUSTOM_FIELD_ROW_HEIGHT} justify="center">
            <SummaryFoldRow
              icon={hideEmpty ? "caret-right" : "caret-up"}
              label={hideEmpty ? "More" : "Less"}
              expanded={!hideEmpty}
              onClick={() => emptyFold.setCollapsed(!hideEmpty)}
            />
          </Box>
        </Box>
      ) : null}

      {/* Add field from workspace — mobile drawer / desktop command dialog.
          The command list does not filter itself; the explicit pass above
          does. */}
      {isMobile ? (
        <AddFieldsDrawer
          open={addFieldDialogOpen}
          onOpenChange={(open: boolean) => {
            setAddFieldDialogOpen(open);
            if (!open) setSelectedFieldIds([]);
          }}
          fields={availableFields}
          selectedIds={selectedFieldIds}
          onToggle={toggleFieldSelection}
          canManage={canManageCustomFields}
          onManage={handleNavigateToCreateField}
          onAdd={handleAddFields}
          query={addFieldQuery}
          onQueryChange={setAddFieldQuery}
        />
      ) : (
        <AddCustomFieldsDialog
          open={addFieldDialogOpen}
          onOpenChange={(open: boolean) => {
            setAddFieldDialogOpen(open);
            if (!open) setSelectedFieldIds([]);
          }}
          attachedFieldIds={fieldIds}
          onAdd={handleAddFieldRows}
          onCreateNew={
            canManageCustomFields ? handleNavigateToCreateField : undefined
          }
          locationName={issue?.name}
        />
      )}
    </Box>
  );
}
